import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

/**
 * BrandButton Component - Professional Financial Design
 *
 * Professional RWA financial platform button variants:
 * - primary: Champagne gold gradient (primary CTA)
 * - secondary: Deep sapphire blue (secondary actions)
 * - outline: Professional outlined with gold border
 * - ghost: Subtle with hover effects
 * - destructive: For dangerous actions (professional red)
 * - link: Text-only button
 *
 * All variants maintain WCAG AA accessibility standards
 */

const brandButtonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-semibold tracking-wide ring-offset-background transition-all duration-300 ease-in-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed",
  {
    variants: {
      variant: {
        primary:
          "bg-gradient-to-r from-brand-primary to-brand-primary-light hover:from-brand-primary-light hover:to-blue-400 text-white shadow-lg shadow-brand-primary/30 hover:shadow-xl hover:shadow-brand-primary/40 hover:scale-105 active:scale-100",
        secondary:
          "bg-blue-50 text-brand-primary hover:bg-blue-100 border border-blue-200 transition-all",
        outline:
          "border-2 border-brand-primary/50 text-brand-primary bg-transparent hover:bg-brand-primary/5 hover:border-brand-primary transition-all",
        ghost:
          "text-text-secondary hover:text-text-primary hover:bg-gray-100 transition-all",
        destructive:
          "bg-gradient-to-r from-danger-dark to-danger hover:from-danger hover:to-danger-light text-white shadow-md hover:shadow-lg",
        link: "text-brand-primary hover:text-brand-primary-light underline-offset-4 hover:underline transition-colors",
        auction:
          "bg-gradient-to-r from-warning-dark to-warning hover:from-warning hover:to-warning-light text-white shadow-lg hover:shadow-xl hover:scale-105",
        uniswap:
          "bg-gradient-to-r from-info-dark to-info hover:from-info hover:to-info-light text-white shadow-lg hover:shadow-xl hover:scale-105",
        pendle:
          "bg-gradient-pendle text-white shadow-lg hover:shadow-xl hover:scale-105",
        success:
          "bg-gradient-to-r from-success-dark to-success hover:from-success hover:to-success-light text-white shadow-md hover:shadow-lg font-semibold",
      },
      size: {
        sm: "h-9 px-4 py-2 text-sm rounded-md",
        default: "h-10 px-6 py-3 text-sm rounded-lg",
        lg: "h-11 px-8 py-3 text-base rounded-lg",
        xl: "h-14 px-10 py-4 text-lg rounded-xl",
        icon: "h-10 w-10 rounded-lg",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
  },
);

export interface BrandButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof brandButtonVariants> {
  asChild?: boolean;
  /**
   * Loading state
   */
  loading?: boolean;
  /**
   * Full width button
   */
  fullWidth?: boolean;
}

const BrandButton = React.forwardRef<HTMLButtonElement, BrandButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading = false,
      fullWidth = false,
      disabled,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(
          brandButtonVariants({ variant, size }),
          fullWidth && "w-full",
          className,
        )}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? (
          <span className="flex items-center gap-2">
            <svg
              className="animate-spin h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span>Processing...</span>
          </span>
        ) : (
          children
        )}
      </Comp>
    );
  },
);

BrandButton.displayName = "BrandButton";

export { BrandButton, brandButtonVariants };

/**
 * Specialized Button Components - Professional Financial
 */

/**
 * Connect Wallet Button - Professional primary CTA
 */
export const ConnectWalletButton = React.forwardRef<
  HTMLButtonElement,
  Omit<BrandButtonProps, "variant">
>(({ children = "Connect Wallet", ...props }, ref) => (
  <BrandButton ref={ref} variant="primary" size="lg" {...props}>
    {children}
  </BrandButton>
));

ConnectWalletButton.displayName = "ConnectWalletButton";

/**
 * Market Action Button - for specific market types
 */
export interface MarketActionButtonProps extends Omit<
  BrandButtonProps,
  "variant"
> {
  marketType: "auction" | "uniswap" | "pendle";
}

export const MarketActionButton = React.forwardRef<
  HTMLButtonElement,
  MarketActionButtonProps
>(({ marketType, ...props }, ref) => (
  <BrandButton ref={ref} variant={marketType} {...props} />
));

MarketActionButton.displayName = "MarketActionButton";

/**
 * Submit Transaction Button - Professional primary action with loading state
 */
export interface SubmitTransactionButtonProps extends Omit<
  BrandButtonProps,
  "variant"
> {
  transacting?: boolean;
}

export const SubmitTransactionButton = React.forwardRef<
  HTMLButtonElement,
  SubmitTransactionButtonProps
>(({ children = "Submit Transaction", transacting = false, ...props }, ref) => (
  <BrandButton
    ref={ref}
    variant="primary"
    size="lg"
    loading={transacting}
    {...props}
  >
    {children}
  </BrandButton>
));

SubmitTransactionButton.displayName = "SubmitTransactionButton";

/**
 * Button Group - for grouped actions
 */
export interface ButtonGroupProps {
  children: React.ReactNode;
  className?: string;
  orientation?: "horizontal" | "vertical";
}

export const ButtonGroup: React.FC<ButtonGroupProps> = ({
  children,
  className,
  orientation = "horizontal",
}) => {
  return (
    <div
      className={cn(
        "inline-flex",
        orientation === "horizontal" ? "flex-row gap-3" : "flex-col gap-2",
        className,
      )}
    >
      {children}
    </div>
  );
};

ButtonGroup.displayName = "ButtonGroup";
